import * as monitoringModuleHelpers from '@/store/monitoring/monitoringModule.helpers'
import * as tripReplayWidgetHelpers from '@/store/monitoring/tripReplayWidget.helpers'
import * as dt from '@/common/dateUtils'
import * as unitSensors from '@/common/unitSensors'
import * as googleMaps from '@/common/googleMaps'
import * as mapUtils from '@/maps/google/mapUtils'
import * as assetMarkers from '@/maps/google/assetMarkers'
import * as eventMarkers from '@/maps/google/eventMarkers'

import ApiService from '@/common/api.service'
import store from '@/store'
import moment from 'moment'

let _tripTelemetry = []
let _tripPolylines = []
let _activeTripOuterPolyline = null
let _replayAsset = null
let _replayInterval = null
let _eventMarkers = []

const _tripColors = [
  '#e53935',
  '#1e88e5',
  '#43a047',
  '#d81b60',
  '#8e24aa',
  '#f4511e',
  '#7cb342',
  '#5e35b1',
  '#00897b',
  '#3949ab',
  '#6d4c41'
]

const plotTrip = (tripIndex, strokeColor, startFromLatLng = null) => {
  if (tripIndex < _tripTelemetry.length) {
    const telemetry = _tripTelemetry[tripIndex]
    const coordinates = []

    if (startFromLatLng !== null) {
      // This is typically the last trip's final lat/lng,
      // we are effectively joining the trips together.
      coordinates.push(startFromLatLng)
    }

    telemetry.forEach((position) => {
      coordinates.push({ lat: position.gps.latitude, lng: position.gps.longitude })
    })

    let tripPolyLine = new google.maps.Polyline({
      path: coordinates,
      geodesic: true,
      strokeColor: strokeColor,
      strokeOpacity: 0.7,
      strokeWeight: 2,
      zIndex: 100,
      icons: []
    })
    tripPolyLine.setMap(googleMaps.map)
    _tripPolylines[tripIndex] = tripPolyLine
  }
}

const clearTelemetryData = () => {
  _tripTelemetry = []
  _tripPolylines.forEach((polyLine) => {
    polyLine.setMap(null)
  })
  _tripPolylines = []

  if (_activeTripOuterPolyline !== null) {
    _activeTripOuterPolyline.setMap(null)
    _activeTripOuterPolyline = null
  }

  if (_replayAsset !== null) {
    monitoringModuleHelpers.removeMarker(_replayAsset, 'replay')
    _replayAsset = null
  }
}

const highlightTripTelemetry = (tripIndex) => {
  _tripPolylines.forEach(polyLine => {
    polyLine.setOptions({
      strokeOpacity: 0.7,
      strokeWeight: 2,
      zIndex: 100,
      icons: []
    })
  })

  if (_activeTripOuterPolyline !== null) {
    _activeTripOuterPolyline.setMap(null)
    _activeTripOuterPolyline = null
  }

  if (tripIndex < _tripPolylines.length) {
    _activeTripOuterPolyline = new google.maps.Polyline({
      path: _tripPolylines[tripIndex].getPath(),
      geodesic: true,
      strokeColor: '#ffffff',
      strokeOpacity: 0.7,
      strokeWeight: 5,
      zIndex: 101
    })
    _activeTripOuterPolyline.setMap(googleMaps.map)

    const color = _tripPolylines[tripIndex].strokeColor
    _tripPolylines[tripIndex].setOptions({
      strokeOpacity: 1.0,
      strokeWeight: 3,
      zIndex: 102,
      icons: [{
        icon: {
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          strokeColor: color,
          strokeWeight: 1,
          fillColor: color,
          fillOpacity: 1.0
        },
        offset: '100px',
        repeat: '150px'
      }]
    })
  }
}

const replayTick = () => {
  const replaySpeed = tripReplayWidgetHelpers.getReplaySpeed()
  if (replaySpeed > 0) {
    tripReplayWidgetHelpers.incRecordIdx()
  } else if (replaySpeed < 0) {
    tripReplayWidgetHelpers.decRecordIdx()
  }
}

const cancelReplayTick = () => {
  if (_replayInterval !== null) {
    window.clearInterval(_replayInterval)
  }
}

const plotTripMarkers = (tripIndex) => {
  if (tripIndex < _tripTelemetry.length) {
    let { markers, tooManyIcons } = eventMarkers.plotEventMarkers(_tripTelemetry[tripIndex], tripMarkerClicked)
    _eventMarkers = markers
    tripReplayWidgetHelpers.setTooManyIcons(tooManyIcons)
  }
}

const clearTripMarkers = () => {
  _eventMarkers.forEach(marker => {
    mapUtils.removeCustomMarker(marker)
    marker = null
  })
  _eventMarkers = []
}

// We intentionally do not use an arrow function callback here
// so that we can use the "this" context of the function instead
// of the module.
function tripMarkerClicked () {
  let marker = this
  tripReplayWidgetHelpers.setReplayMode('pause')
  tripReplayWidgetHelpers.setRecordIdx(marker.data.positionIndex, true)
  tripReplayWidgetHelpers.setViewMode('details')
}

/**
 * Returns the trip telemetry count based based on the trip index.
 *
 * @param tripIdx {int}
 *
 * @return {int}
 */
const tripRecordCount = (tripIdx) => {
  return _tripTelemetry[tripIdx].length
}

/**
 * Returns a specific telemetry record within a specific trip.
 *
 * @param tripIdx {int}
 * @param recordIdx {int}
 *
 * @return {Object}
 */
const tripRecord = (tripIdx, recordIdx) => {
  return _tripTelemetry[tripIdx][recordIdx]
}

/**
 * Returns the first telemetry record within a specific trip.
 *
 * @param tripIdx {int}
 *
 * @return {Object}
 */
const tripFirstRecord = (tripIdx) => {
  return _tripTelemetry[tripIdx][0]
}

/**
 * Returns the last telemetry record within a specific trip.
 *
 * @param tripIdx {int}
 *
 * @return {Object}
 */
const tripLastRecord = (tripIdx) => {
  return _tripTelemetry[tripIdx][_tripTelemetry[tripIdx].length - 1]
}

/**
 * Returns the total number of loaded trips.
 *
 * @returns {int}
 */
const tripCount = () => {
  return _tripTelemetry.length
}

/**
 * Returns the index of the last trip (i.e. count - 1)
 */
const lastTripIndex = () => {
  return _tripTelemetry.length - 1
}

export const state = {
  vehicle: null, // Vehicle which trips are being viewed
  unit: null, // Unit which trips are being viewed
  date: dt.dateShort(dt.now()), // Currently viewed date
  isTodaysTrips: true,
  trips: [],
  selectedTrip: null,
  selectedTripIdx: 0,
  telemetry: null,
  recordIdx: 0,
  viewMode: 'trips', // Can be 'trips' (trip / date selection), 'details', 'options'
  isFirstRecord: true,
  isLastRecord: false,
  isFirstTrip: true,
  isLastTrip: false,
  tooManyIcons: false,
  tripProgress: 0, // Progress percentage within the current trip
  replaySpeed: 0,
  replayStepSize: 0,
  replayMode: 'pause', // Can be 'pause', 'play', 'rewind', 'fast-forward'
  prevQueriedVehicleId: null,
  prevQueriedAssignedAs: null,
  prevQueriedUnitId: null,
  prevQueriedDate: null
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  getVehicle (state) {
    return state.vehicle
  },

  getUnit (state) {
    return state.unit
  },

  getDate (state) {
    return state.date
  },

  getIsTodaysTrips (state) {
    return state.isTodaysTrips
  },

  getTrips (state) {
    return state.trips
  },

  getSelectedTrip (state) {
    return state.selectedTrip
  },

  getViewMode (state) {
    return state.viewMode
  },

  getReplaySpeed (state) {
    return state.replaySpeed
  },

  getIsFirstRecord (state) {
    return state.isFirstRecord
  },

  getIsLastRecord (state) {
    return state.isLastRecord
  },

  getIsFirstTrip (state) {
    return state.isFirstTrip
  },

  getIsLastTrip (state) {
    return state.isLastTrip
  },

  getReplayMode (state) {
    return state.replayMode
  },

  getTripProgress (state) {
    return state.tripProgress
  },

  getPrevQueriedVehicleId (state) {
    return state.prevQueriedVehicleId
  },

  getPrevQueriedAssignedAs (state) {
    return state.prevQueriedAssignedAs
  },

  getPrevQueriedUnitId (state) {
    return state.prevQueriedUnitId
  },

  getPrevQueriedDate (state) {
    return state.prevQueriedDate
  },

  /**
   * Get the current telemetry for the point in time in the replay.
   *
   * @param state
   * @returns null|Object
   */
  getTelemetry (state) {
    return state.telemetry
  },

  getTooManyIcons (state) {
    return state.tooManyIcons
  }
}

export const actions = {
  queryVehicleTrips (context) {
    const vehicle = context.getters['getVehicle']
    const date = context.getters['getDate']
    return ApiService
      .get(`history/trips/vehicle/${vehicle.id}/${vehicle.unit_assigned_as}/date/${date}/all`)
      .then((response) => {
        store.commit(`monitoring/tripReplayWidgetModule/setTrips`, response.data.data)
        return store.dispatch(`monitoring/tripReplayWidgetModule/queryVehicleTelemetry`)
      })
  },

  queryUnitTrips (context) {
    const unit = context.getters['getUnit']
    const date = context.getters['getDate']
    return ApiService
      .get(`history/trips/unit/${unit.id}/date/${date}/all`)
      .then((response) => {
        store.commit(`monitoring/tripReplayWidgetModule/setTrips`, response.data.data)
        return store.dispatch(`monitoring/tripReplayWidgetModule/queryUnitTelemetry`)
      })
  },

  queryTrips (context) {
    if (context.getters['getVehicle'] !== null) {
      return context.dispatch('queryVehicleTrips')
    } else if (context.getters['getUnit'] !== null) {
      return context.dispatch('queryUnitTrips')
    } else {
      return Promise.resolve(null)
    }
  },

  queryVehicleTelemetry (context) {
    const trips = context.getters['getTrips']
    const vehicle = context.getters['getVehicle']
    const prevQueriedVehicleId = context.getters['getPrevQueriedVehicleId']
    const prevQueriedAssignedAs = context.getters['getPrevQueriedAssignedAs']
    const prevQueriedDate = context.getters['getPrevQueriedDate']

    // Get the first trip start time and the last trip end time
    if (trips.length > 0) {
      const from = moment(trips[0].from.gps.local_time)
      let to = null
      if (trips[trips.length - 1].to.gps.local_time === null) {
        // The trip is still active, so set the "to" time to the
        // end of the day.
        to = moment(trips[0].from.gps.local_time).endOf('day')
      } else {
        to = moment(trips[trips.length - 1].to.gps.local_time)
      }

      const queryFromDate = dt.dateShort(from)
      const queryFromTime = dt.time(from)
      const queryToDate = dt.dateShort(to)
      const queryToTime = dt.time(to)

      // Only perform the query if we haven't just queried with the same arguments
      const canQuery = prevQueriedVehicleId !== vehicle.id ||
        prevQueriedAssignedAs !== vehicle.unit_assigned_as ||
        prevQueriedDate !== queryFromDate

      if (canQuery) {
        context.commit(`setPrevQueriedVehicle`, { prevQueriedVehicleId: vehicle.id, prevQueriedAssignedAs: vehicle.unit_assigned_as })
        context.commit(`setPrevQueriedUnitId`, null)
        context.commit(`setPrevQueriedDate`, queryFromDate)
        return ApiService
          .get(`history/trips/telemetry/vehicle/` +
            `${vehicle.id}/${vehicle.unit_assigned_as}/period/from/${queryFromDate}/time/${queryFromTime}/to/${queryToDate}/time/${queryToTime}?`)
          .then((response) => {
            store.commit(`monitoring/tripReplayWidgetModule/setLoadedTelemetry`, response.data.data)
            return response
          })
      }
    }

    return Promise.resolve(null)
  },

  queryUnitTelemetry (context) {
    const trips = context.getters['getTrips']
    const unit = context.getters['getUnit']
    const prevQueriedUnitId = context.getters['getPrevQueriedUnitId']
    const prevQueriedDate = context.getters['getPrevQueriedDate']

    // Get the first trip start time and the last trip end time
    if (trips.length > 0) {
      const from = moment(trips[0].from.gps.local_time)
      let to = null
      if (trips[trips.length - 1].to.gps.local_time === null) {
        // The trip is still active, so set the "to" time to the
        // end of the day.
        to = moment(trips[0].from.gps.local_time).endOf('day')
      } else {
        to = moment(trips[trips.length - 1].to.gps.local_time)
      }

      const queryFromDate = dt.dateShort(from)
      const queryFromTime = dt.time(from)
      const queryToDate = dt.dateShort(to)
      const queryToTime = dt.time(to)

      // Only perform the query if we haven't just queried with the same arguments
      const canQuery = prevQueriedUnitId !== unit.id ||
        prevQueriedDate !== queryFromDate

      if (canQuery) {
        context.commit(`setPrevQueriedVehicle`, { prevQueriedVehicleId: null, prevQueriedAssignedAs: null })
        context.commit(`setPrevQueriedUnitId`, unit.id)
        context.commit(`setPrevQueriedDate`, queryFromDate)

        return ApiService
          .get(`history/trips/telemetry/unit/` +
            `${unit.id}/period/from/${queryFromDate}/time/${queryFromTime}/to/${queryToDate}/time/${queryToTime}`)
          .then((response) => {
            store.commit(`monitoring/tripReplayWidgetModule/setLoadedTelemetry`, response.data.data)
            return response
          })
      }
    }

    return Promise.resolve(null)
  }
}

export const mutations = {
  setVehicle (state, vehicle) {
    if (state.vehicle === null && vehicle !== null) {
      state.vehicle = vehicle
      state.unit = null
      store.commit('monitoring/tripReplayWidgetModule/clearTrips')
      store.dispatch(`monitoring/tripReplayWidgetModule/queryVehicleTrips`)
    } else if (state.vehicle !== null && vehicle === null) {
      state.vehicle = vehicle
      state.unit = null
      store.commit('monitoring/tripReplayWidgetModule/clearTrips')
    } else if (state.vehicle !== null && vehicle !== null && state.vehicle.id !== vehicle.id) {
      state.vehicle = vehicle
      state.unit = null
      store.commit('monitoring/tripReplayWidgetModule/clearTrips')
      store.dispatch(`monitoring/tripReplayWidgetModule/queryVehicleTrips`)
    }
  },

  setUnit (state, unit) {
    if (state.unit === null && unit !== null) {
      state.vehicle = null
      state.unit = unit
      store.commit('monitoring/tripReplayWidgetModule/clearTrips')
      store.dispatch(`monitoring/tripReplayWidgetModule/queryUnitTrips`)
    } else if (state.unit !== null && unit === null) {
      state.vehicle = null
      state.unit = unit
      store.commit('monitoring/tripReplayWidgetModule/clearTrips')
    } else if (state.unit !== null && unit !== null && state.unit.id !== unit.id) {
      state.vehicle = null
      state.unit = unit
      store.commit('monitoring/tripReplayWidgetModule/clearTrips')
      store.dispatch(`monitoring/tripReplayWidgetModule/queryUnitTrips`)
    }
  },

  setDate (state, date) {
    if (date !== state.date || state.trips.length === 0) {
      state.date = date
      state.isTodaysTrips = dt.dateShort(dt.now()) === state.date
      store.commit(`monitoring/tripReplayWidgetModule/clearTrips`)
      return store.dispatch(`monitoring/tripReplayWidgetModule/queryTrips`)
    }
  },

  setTrips (state, trips) {
    let trip_no = 0
    let total_distance_km = 0
    let total_secs = 0
    let total_driving_secs = 0
    let total_idling_secs = 0
    let trip_color_idx = 0
    trips.forEach(trip => {
      trip_no += 1
      total_distance_km += trip.summary.gps.distance_km
      total_secs += trip.summary.durations.total_secs
      total_driving_secs += trip.summary.durations.driving_secs
      total_idling_secs += trip.summary.durations.idling_secs
      // trip['telemetry'] = []
      trip['trip_no'] = trip_no
      trip['trip_idx'] = trip_no - 1
      trip['trip_color'] = _tripColors[trip_color_idx]
      trip['summary']['total_distance_km'] = total_distance_km
      trip['summary']['total_time'] = unitSensors.secsToHMS(total_secs)
      trip['summary']['total_driving_time'] = unitSensors.secsToHMS(total_driving_secs)
      trip['summary']['total_idling_time'] = unitSensors.secsToHMS(total_idling_secs)
      trip['bounds'] = null

      trip_color_idx += 1
      if (trip_color_idx > _tripColors.length - 1) {
        trip_color_idx = 0
      }
    })
    state.trips = trips
  },

  clearTrips (state) {
    state.trips = []
    state.selectedTrip = null
    state.selectedTripIdx = 0
    state.recordIdx = 0
    state.isFirstRecord = true
    state.isLastRecord = false
    state.isFirstTrip = true
    state.isLastTrip = false
    state.tooManyIcons = false
    state.tripProgress = 0
    state.replaySpeed = 0
    state.replayStepSize = 0
    state.replayMode = 'pause'
    state.telemetry = null
    state.prevQueriedVehicleId = null
    state.prevQueriedAssignedAs = null
    state.prevQueriedUnitId = null
    state.prevQueriedDate = null

    cancelReplayTick()
    clearTelemetryData()
    clearTripMarkers()
  },

  /**
   * Selects the next available trip. An optional position of
   * 'first' or 'last' can be provided to jump to the first or
   * last record in the trip.
   *
   * @param state
   * @param position string Can be 'first', 'last' to jump to the first/last record.
   */
  selectNextTrip (state, position = 'first') {
    store.commit(`monitoring/tripReplayWidgetModule/setSelectedTrip`, {
      tripIdx: state.selectedTripIdx + 1,
      position
    })
  },

  /**
   * Selects the previous trip. An optional position of
   * 'first' or 'last' can be provided to jump to the first or
   * last record in the trip.
   *
   * @param state
   * @param position string Can be 'first', 'last' to jump to the first/last record.
   */
  selectPreviousTrip (state, position = 'first') {
    store.commit(`monitoring/tripReplayWidgetModule/setSelectedTrip`, {
      tripIdx: state.selectedTripIdx - 1,
      position
    })
  },

  /**
   * Set the selected trip by specifying the trip's index. An
   * optional position of 'first' or 'last' can be provided
   * to jump to the first or last record in the trip.
   *
   * @param state
   * @param tripIdx {int}
   * @param position {string} Can be 'first' or 'last'
   */
  setSelectedTrip (state, { tripIdx, position = 'first' }) {
    if (tripCount() === 0) {
      return
    }

    if (state.selectedTrip === null) {
      // Unfollow all vehicles so the map doesn't attempt
      // to follow a real-time vehicle while the user is
      // trying to do a replay
      monitoringModuleHelpers.unfollowAll()

      // Make all the existing assets markers on the map
      // transparent so that they don't interfere with
      // the replay's marker. When the widget closes, they
      // will be restored to normal opacity.
      assetMarkers.setAllMarkersOpacity(0.5)
    }

    if (tripIdx < 0) {
      state.selectedTripIdx = 0
      store.commit(`monitoring/tripReplayWidgetModule/setReplayMode`, 'pause')
    } else if (tripIdx > lastTripIndex()) {
      state.selectedTripIdx = lastTripIndex()
      store.commit(`monitoring/tripReplayWidgetModule/setReplayMode`, 'pause')
    } else {
      state.selectedTripIdx = tripIdx
    }

    state.isFirstTrip = state.selectedTripIdx === 0
    state.isLastTrip = state.selectedTripIdx === lastTripIndex()
    state.selectedTrip = state.trips[state.selectedTripIdx]

    // Remove previous markers
    clearTripMarkers()

    // Highlight the trip
    highlightTripTelemetry(state.selectedTripIdx)

    // Add the event markers
    plotTripMarkers(state.selectedTripIdx)

    let telemetry = null
    if (tripRecordCount(state.selectedTripIdx) > 0) {
      if (position === 'first') {
        // Get the first trip record
        telemetry = tripFirstRecord(state.selectedTripIdx)
        if (state.isFirstTrip) {
          state.isFirstRecord = true
          state.isLastRecord = false
        }
        state.recordIdx = 0
        state.tripProgress = 0
      } else if (position === 'last') {
        // Get the last trip record
        telemetry = tripLastRecord(state.selectedTripIdx)
        if (state.isLastTrip) {
          state.isLastRecord = true
          state.isFirstRecord = false
        }
        state.recordIdx = tripRecordCount(state.selectedTripIdx) - 1
        state.tripProgress = 100
      }
    }

    // Add/update a trip replay marker to the map for the unit/vehicle
    if (telemetry !== null) {
      googleMaps.map.panTo({ lat: telemetry.gps.latitude, lng: telemetry.gps.longitude })
      let existingAsset = null
      if (state.vehicle !== null) {
        existingAsset = monitoringModuleHelpers.getMonitoredVehicle(state.vehicle.id, state.vehicle.unit_assigned_as)
      } else if (state.unit !== null) {
        existingAsset = monitoringModuleHelpers.getMonitoredUnit(state.unit.id)
      }

      if (existingAsset !== null) {
        _replayAsset = Object.assign({}, existingAsset)
        _replayAsset.telemetry = telemetry
        monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
      }
    }
  },

  setLoadedTelemetry (state, telemetry) {
    // Add the telemetry for each trip based on the trip's from/to local_time timestamps.
    let tripIdx = 0
    let tmpTelemetry = []

    // Note, initial values set to the opposite values
    // they usually are because we'll be reducing/increasing
    // their values based on the minimum and maximum lat/lng
    // coordinates of each position.
    let boundsNorthLat = -90.0
    let boundsSouthLat = 90.0
    let boundsEastLng = -180.0
    let boundsWestLng = 180.0
    let isEndOfTrip = false

    telemetry.forEach((position, telemetryIndex) => {
      // Check if the trip has an "to" local_time (if not it could mean that the
      // the trip is still active).
      if (tripIdx < state.trips.length) {
        if (state.trips[tripIdx].to.gps.local_time === null) {
          // This trip does not have a "to" local_time.
          isEndOfTrip = telemetryIndex === telemetry.length - 1
        } else {
          // This trip does have a "to" local_time
          isEndOfTrip = telemetryIndex === telemetry.length - 1 ||
            position.gps.local_time > state.trips[tripIdx].to.gps.local_time
        }
      }

      if (isEndOfTrip) {
        // Set the trips boundaries
        state.trips[tripIdx].bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(boundsSouthLat, boundsWestLng), // South-West
          new google.maps.LatLng(boundsNorthLat, boundsEastLng) // North-East
        )

        // Reset the boundaries
        boundsNorthLat = -90.0
        boundsSouthLat = 90.0
        boundsEastLng = -180.0
        boundsWestLng = 180.0

        // Push the array of telemetry data into the trip array
        _tripTelemetry.push([...tmpTelemetry])
        tmpTelemetry = []
        tripIdx += 1
        isEndOfTrip = false
      }

      if (position.gps.latitude > boundsNorthLat) boundsNorthLat = position.gps.latitude
      if (position.gps.latitude < boundsSouthLat) boundsSouthLat = position.gps.latitude
      if (position.gps.longitude > boundsEastLng) boundsEastLng = position.gps.longitude
      if (position.gps.longitude < boundsWestLng) boundsWestLng = position.gps.longitude

      tmpTelemetry.push(position)
    })

    store.commit(`monitoring/tripReplayWidgetModule/plotAllTrips`)
    if (state.trips.length > 0) {
      store.commit(`monitoring/tripReplayWidgetModule/setSelectedTrip`, {
        tripIdx: 0
      })
    }
  },

  plotAllTrips (state) {
    let prevLatLng = null
    state.trips.forEach((trip) => {
      plotTrip(trip.trip_no - 1, trip.trip_color, prevLatLng)
      prevLatLng = {
        lat: trip.to.gps.latitude,
        lng: trip.to.gps.longitude
      }
    })
  },

  /**
   * Set the current telemetry record for the point in time in the replay.
   *
   * @param state
   * @param telemetry
   */
  setTelemetry (state, telemetry) {
    state.telemetry = telemetry
  },

  setViewMode (state, viewMode) {
    state.viewMode = viewMode
  },

  /**
   * Sets the current replay speed.
   *
   * 0 = Paused, -1 = Rewind, 1 = Play, < -1 = Rewind Fast, > 1 Forward Fast
   *
   * @param state
   * @param replaySpeed
   */
  setReplaySpeed (state, replaySpeed) {
    state.replaySpeed = replaySpeed
    // _replaySpeed = replaySpeed
    if (replaySpeed === 0) { // pause
      state.replayStepSize = 1
      cancelReplayTick()
    } else if (replaySpeed === 1 || replaySpeed === -1) { // play/rewind
      cancelReplayTick()
      state.replayStepSize = 1
      _replayInterval = window.setInterval(replayTick, 800)
    } else if (replaySpeed === 2 || replaySpeed === -1) { // fast forward/fast rewind
      cancelReplayTick()
      state.replayStepSize = 1
      _replayInterval = window.setInterval(replayTick, 400)
    } else if (replaySpeed > 2 || replaySpeed < -2) { // faster fast-forward/rewind
      cancelReplayTick()
      state.replayStepSize += 2
      _replayInterval = window.setInterval(replayTick, 400)
    }
  },

  setReplayMode (state, replayMode) {
    if (state.replayMode !== replayMode) {
      switch (replayMode) {
        case 'pause':
          store.commit(`monitoring/tripReplayWidgetModule/setReplaySpeed`, 0)
          break

        case 'play':
          store.commit(`monitoring/tripReplayWidgetModule/setReplaySpeed`, 1)
          break

        case 'rewind':
          store.commit(`monitoring/tripReplayWidgetModule/setReplaySpeed`, -1)
          break

        case 'fast-forward':
          store.commit(`monitoring/tripReplayWidgetModule/setReplaySpeed`, 2)
          break
      }
      state.replayMode = replayMode
    }
  },

  incReplaySpeed (state) {
    store.commit(`monitoring/tripReplayWidgetModule/setReplaySpeed`, state.replaySpeed + 1)
  },

  decReplaySpeed (state) {
    store.commit(`monitoring/tripReplayWidgetModule/setReplaySpeed`, state.replaySpeed - 1)
  },

  /**
   * Increments the active record within the current trip.
   *
   * @param state
   */
  incRecordIdx (state) {
    state.recordIdx += state.replayStepSize
    if (state.recordIdx > tripRecordCount(state.selectedTripIdx) - 1) {
      // Are we at the end of the last trip? If not, we jump to the start
      // of the next trip. If we are at the last trip already there is
      // nothing left to do so we'll pause the replay.
      if (state.selectedTripIdx < lastTripIndex()) {
        // state.recordIdx = 0
        // state.tripProgress = 0
        store.commit(`monitoring/tripReplayWidgetModule/selectNextTrip`, 'first')
      } else {
        state.recordIdx = tripRecordCount(state.selectedTripIdx) - 1
        state.tripProgress = 100
        state.isLastRecord = true
        store.commit(`monitoring/tripReplayWidgetModule/setReplayMode`, 'pause')
      }
    } else {
      state.tripProgress = Math.ceil(state.recordIdx / (tripRecordCount(state.selectedTripIdx) - 1) * 100)
      state.isLastRecord = false
      if (state.recordIdx > 0) {
        state.isFirstRecord = false
      }
    }

    _replayAsset.telemetry = tripRecord(state.selectedTripIdx, state.recordIdx)
    monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
    store.commit(`monitoring/tripReplayWidgetModule/setTelemetry`, _replayAsset.telemetry)
  },

  /**
   * Decrements the active record within the current trip.
   *
   * @param state
   */
  decRecordIdx (state) {
    state.recordIdx -= state.replayStepSize
    if (state.recordIdx < 0) {
      // Are we at the start of the first trip? If not, we jump to the end of the
      // previous trip, if we are there is nothing left to do so we'll pause
      // the replay.
      if (state.selectedTripIdx > 0) {
        // state.recordIdx = tripRecordCount(state.selectedTripIdx - 1) - 1
        // state.tripProgress = 100
        store.commit(`monitoring/tripReplayWidgetModule/selectPreviousTrip`, 'last')
      } else {
        state.recordIdx = 0
        state.tripProgress = 0
        state.isFirstRecord = true
        store.commit(`monitoring/tripReplayWidgetModule/setReplayMode`, 'pause')
      }
    } else {
      state.tripProgress = Math.ceil(state.recordIdx / (tripRecordCount(state.selectedTripIdx) - 1) * 100)
      state.isFirstRecord = false
      if (state.recordIdx < tripRecordCount(state.selectedTripIdx) - 1) {
        state.isLastRecord = false
      }
    }

    _replayAsset.telemetry = tripRecord(state.selectedTripIdx, state.recordIdx)
    monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
    store.commit(`monitoring/tripReplayWidgetModule/setTelemetry`, _replayAsset.telemetry)
  },

  setRecordIdx (state, { recordIdx, panTo }) {
    const lastRecordIdx = tripRecordCount(state.selectedTripIdx) - 1
    if (recordIdx >= 0 && state.recordIdx <= lastRecordIdx) {
      state.recordIdx = recordIdx
      state.tripProgress = Math.ceil(state.recordIdx / lastRecordIdx * 100)
      if (recordIdx === 0) {
        state.isFirstRecord = true
        state.isFirstRecord = false
      } else if (recordIdx === lastRecordIdx) {
        state.isLastRecord = true
        state.isFirstRecord = false
      }
      _replayAsset.telemetry = tripRecord(state.selectedTripIdx, state.recordIdx)
      monitoringModuleHelpers.setMarker(_replayAsset, 'replay')
      store.commit(`monitoring/tripReplayWidgetModule/setTelemetry`, _replayAsset.telemetry)

      if (panTo) {
        googleMaps.panToLatLng(
          googleMaps.map,
          _replayAsset.telemetry.gps.latitude,
          _replayAsset.telemetry.gps.longitude
        )
        if (googleMaps.map.getZoom() < 10) {
          googleMaps.map.setZoom(15)
        }
      }
    }
  },

  setPrevQueriedVehicle (state, { prevQueriedVehicleId, prevQueriedAssignedAs }) {
    state.prevQueriedVehicleId = prevQueriedVehicleId
    state.prevQueriedAssignedAs = prevQueriedAssignedAs
  },

  setPrevQueriedUnitId (state, prevQueriedUnitId) {
    state.prevQueriedUnitId = prevQueriedUnitId
  },

  setPrevQueriedDate (state, prevQueriedDate) {
    state.prevQueriedDate = prevQueriedDate
  },

  setTooManyIcons (state, tooManyIcons) {
    state.tooManyIcons = tooManyIcons
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.vehicle = null
    state.unit = null
    state.date = dt.dateShort(dt.now())
    state.isTodaysTrips = true
    store.commit(`monitoring/tripReplayWidgetModule/clearTrips`)
  },

  updateTripMarkers (state) {
    clearTripMarkers()
    plotTripMarkers(state.selectedTripIdx)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/monitoring/tripReplayWidgetModule.js